import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { adminDestinationLocationInclusionsPath } from "@travily/routes";
import type { AdminLocationsRow } from "@travily/types/models";
import { SearchLg } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { TableBody } from "react-aria-components";

function formatDistance(meters: number | null): string {
  if (meters == null) return "-";
  if (meters < 1000) return `${meters} m`;
  return `${Math.round(meters / 1000)} km`;
}

interface AddLocationsProps {
  destinationId: number;
  linkedLocationCount: number;
  onInclude: (locationId: number) => void;
}

export function AddLocations({ destinationId, linkedLocationCount, onInclude }: AddLocationsProps) {
  const [query, setQuery] = useState("");
  const [locations, setLocations] = useState<AdminLocationsRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchLocations = useCallback(
    async (q?: string) => {
      setIsLoading(true);
      const params = q && q.length >= 2 ? `?q=${encodeURIComponent(q)}` : "";
      const url = `${adminDestinationLocationInclusionsPath(destinationId)}${params}`;

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setLocations(await response.json());
      }
      setIsLoading(false);
    },
    [destinationId],
  );

  // Debounced search + refetch when linked locations change
  useEffect(() => {
    void linkedLocationCount;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchLocations(query.length >= 2 ? query : undefined), 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchLocations, linkedLocationCount]);

  const isSearching = query.length >= 2;

  return (
    <TableCard.Root>
      <TableCard.Header
        title={isSearching ? "Search results" : "Nearby locations"}
        badge={`${locations.length}`}
        description={
          isSearching
            ? "Locations matching your search that aren't linked to this destination."
            : "Unlinked locations near this destination, sorted by proximity."
        }
        contentTrailing={
          <Input
            size="sm"
            icon={SearchLg}
            placeholder="Search locations..."
            aria-label="Search locations"
            value={query}
            onChange={(value) => setQuery(value)}
            className="w-full max-w-70"
          />
        }
        className="pb-5"
      />
      {locations.length > 0 ? (
        <Table aria-label="Available locations">
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader />
            <Table.Head id="address" label="Address" />
            <Table.Head id="country" label="Country" />
            <Table.Head id="distance" label="Distance" />
            <Table.Head id="actions" />
          </Table.Header>
          <TableBody items={locations}>
            {(location) => (
              <Table.Row key={location.id} id={location.id}>
                <Table.Cell>
                  <span className="text-sm font-medium text-primary">{location.name}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{location.full_address ?? "-"}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{location.country_code}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{formatDistance(location.distance_meters)}</span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center justify-end gap-3">
                    <Button size="sm" color="link-color" onClick={() => onInclude(location.id)}>
                      Include
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      ) : (
        <div className="px-6 py-8 text-center text-sm text-tertiary">
          {isLoading
            ? "Loading..."
            : isSearching
              ? "No locations found matching your search."
              : "No nearby unlinked locations found."}
        </div>
      )}
    </TableCard.Root>
  );
}
